<template>
  <div class="container mx-auto px-4 py-8 max-w-6xl">
    <div class="flex justify-between items-center mb-6">
      <div>
        <h1 class="text-3xl font-bold text-gray-900">Q&A</h1>
        <p class="mt-2 text-gray-600">궁금한 점을 질문하고 답변을 확인하세요</p>
      </div>
      
      <!-- 질문 작성 버튼 -->
      <div v-if="authStore.isAuthenticated" class="flex gap-2">
        <RouterLink
          to="/qna/create"
          class="btn btn-primary btn-sm"
        >
          <PlusIcon class="w-5 h-5 mr-2" />
          질문하기
        </RouterLink>
      </div>
    </div>

    <BoardSearchBar
      v-model="searchQuery"
      placeholder="제목, 내용으로 검색..."
      :show-button="true"
      :disabled="loading"
      @search="search"
    />

    <div class="mt-4 mb-4">
      <BoardListToolbar
        v-model:pageSize="pageSize"
        :page-size-options="pageSizeOptions"
        :total-elements="totalElements"
        count-label="질문"
        :show-page-size="true"
        @page-size-change="handlePageSizeChange"
      />
    </div>

    <!-- Q&A 목록 -->
    <div class="bg-white rounded-lg shadow-sm border">
      <div v-if="loading" class="p-8 text-center">
        <div class="spinner w-8 h-8 mx-auto mb-4"></div>
        <p class="text-gray-600">Q&A를 불러오는 중...</p>
      </div>

      <div v-else-if="qnas.length === 0" class="p-12 text-center">
        <QuestionMarkCircleIcon class="w-16 h-16 text-gray-300 mx-auto mb-4" />
        <h3 class="text-lg font-medium text-gray-900 mb-2">질문이 없습니다</h3>
        <p class="text-gray-600">첫 번째 질문을 등록해보세요.</p>
        <RouterLink v-if="authStore.isAuthenticated" to="/qna/create" class="btn btn-primary btn-sm mt-4">
          질문하기
        </RouterLink>
      </div>

      <div v-else>
        <!-- 헤더 -->
        <div class="border-b border-gray-200 px-6 py-3 bg-gray-50">
          <div class="grid grid-cols-12 gap-4 text-sm font-medium text-gray-700">
            <div class="col-span-6">제목</div>
            <div class="col-span-2">작성자</div>
            <div class="col-span-2">작성일</div>
            <div class="col-span-2 text-center">관리</div>
          </div>
        </div>

        <!-- 목록 -->
        <div class="divide-y divide-gray-200">
          <div
            v-for="qna in qnas"
            :key="qna.id"
            class="px-6 py-4 hover:bg-gray-50 transition-colors"
          >
            <div class="grid grid-cols-12 gap-4 items-center">
              <div class="col-span-6">
                <RouterLink
                  :to="`/qna/${qna.id}`"
                  class="text-primary-600 hover:text-primary-700 font-medium"
                >
                  {{ qna.title }}
                </RouterLink>
              </div>
              <div class="col-span-2 text-sm text-gray-600">
                {{ qna.author.name }}
              </div>
              <div class="col-span-2 text-sm text-gray-600">
                {{ formatDate(qna.createdAt) }}
              </div>
              <div class="col-span-2 text-center">
                <div v-if="canManageQna(qna)" class="flex justify-center gap-2">
                  <RouterLink
                    :to="`/qna/${qna.id}/edit`"
                    class="text-blue-600 hover:text-blue-700 text-sm"
                  >
                    <PencilIcon class="w-4 h-4" />
                  </RouterLink>
                  <button
                    @click="deleteQna(qna.id)"
                    class="text-red-600 hover:text-red-700 text-sm"
                  >
                    <TrashIcon class="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="totalPages > 1" class="mt-8">
      <Pagination :current-page="currentPage" :total-pages="totalPages" @page-change="changePage" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, watch } from 'vue'
import { RouterLink } from 'vue-router'
import {
  PlusIcon,
  QuestionMarkCircleIcon,
  PencilIcon,
  TrashIcon
} from '@heroicons/vue/24/outline'
import { useAuthStore } from '@/stores/auth'
import { qnaApi } from '@/services/api'
import type { Qna } from '@/types'
import { useToast } from 'vue-toastification'
import Pagination from '@/components/common/Pagination.vue'
import BoardSearchBar from '@/components/common/BoardSearchBar.vue'
import BoardListToolbar from '@/components/common/BoardListToolbar.vue'

const authStore = useAuthStore()
const toast = useToast()

const qnas = ref<Qna[]>([])
const loading = ref(false)
const searchQuery = ref('')
const currentPage = ref(0)
const pageSizeOptions = [6, 12, 18, 21]
const pageSize = ref(6)
const totalElements = ref(0)
const totalPages = ref(0)

const formatDate = (dateString: string) => {
  const date = new Date(dateString)
  return date.toLocaleDateString('ko-KR')
}

const canManageQna = (qna: Qna) => {
  return authStore.isAdmin || (authStore.user && qna.author.id === authStore.user.id)
}

const loadQnas = async () => {
  try {
    loading.value = true
    const response = await qnaApi.getQnas({
      keyword: searchQuery.value || undefined,
      page: currentPage.value,
      size: pageSize.value
    })
    
    qnas.value = response.content
    totalElements.value = response.totalElements
    totalPages.value = response.totalPages
  } catch (error: any) {
    console.error('Failed to load qnas:', error)
    toast.error('Q&A 목록을 불러오는데 실패했습니다.')
  } finally {
    loading.value = false
  }
}

const search = () => {
  currentPage.value = 0
  loadQnas()
}

const changePage = (page: number) => {
  currentPage.value = page
  loadQnas()
}

const handlePageSizeChange = () => {
  currentPage.value = 0
  loadQnas()
}

const deleteQna = async (id: number) => {
  if (!confirm('정말 삭제하시겠습니까?')) return

  try {
    await qnaApi.deleteQna(id)
    toast.success('Q&A가 삭제되었습니다.')
    loadQnas()
  } catch (error: any) {
    console.error('Failed to delete qna:', error)
    toast.error('Q&A 삭제에 실패했습니다.')
  }
}

watch(searchQuery, () => {
  if (searchQuery.value === '') {
    search()
  }
})

onMounted(() => {
  loadQnas()
})
</script>
